import {
  type GetTagsByCategoryQuery,
  type SearchLearningObjectiveStatementsQuery,
} from '@exam-management/graphql';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { mergeSx, Table, TableContainer, type TableContainerProps } from '@shared/components';
import { useMemo } from 'react';

import { QBankStructureDashBoardTableHeader } from '../../components';
import { type SingleQuestionPropertiesData } from '../../types';
import { getShallowestEnabledNonCourseLevel } from '../../utils';
import { QBankStructureItemSetQuestionDashboardTableBody } from '../qbank-structure-item-set-question-dashboard-table-body';

import { formatDataToQBankStructureLosSingleQuestionDashboardTable } from './utils';

type LearningObjectiveStatement =
  SearchLearningObjectiveStatementsQuery['qbsSearchLearningObjectiveStatements']['learningObjectiveStatements'][number];

type QuestionTag = GetTagsByCategoryQuery['qbsGetTagsByCategory'][number];

interface QBankStructureLosSingleQuestionDashboardTableProps extends TableContainerProps {
  data: SingleQuestionPropertiesData[];
  los: LearningObjectiveStatement[];
  difficultyTags: QuestionTag[];
  calculationTags: QuestionTag[];
}

export default function QBankStructureLosSingleQuestionDashboardTable(
  props: Readonly<QBankStructureLosSingleQuestionDashboardTableProps>,
) {
  const { data, los, difficultyTags, calculationTags, ...restProps } = props;

  const { qBankStructureLevelItems, qBankStructureLevels } = useQBankStructure();

  const defaultLevel = getShallowestEnabledNonCourseLevel(qBankStructureLevels);

  const formattedData = useMemo(
    () =>
      formatDataToQBankStructureLosSingleQuestionDashboardTable({
        qBankStructureLevelItems,
        los,
        questionPropertiesData: data,
        difficultyTags,
        calculationTags,
      }),
    [qBankStructureLevelItems, difficultyTags, calculationTags, los, data],
  );

  if (!defaultLevel) {
    return null;
  }

  return (
    <TableContainer
      {...restProps}
      sx={mergeSx(
        {
          overflow: 'hidden',
        },
        restProps.sx,
      )}
    >
      <Table
        stickyHeader
        size='small'
        sx={{
          border: 'none',
          borderCollapse: 'initial',
          mb: '-1px',
        }}
      >
        <QBankStructureDashBoardTableHeader />
        <QBankStructureItemSetQuestionDashboardTableBody
          rows={formattedData}
          defaultLevel={defaultLevel.category}
        />
      </Table>
    </TableContainer>
  );
}
